import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { App, Descriptions, Image, Button, Space, Spin, Row, Col, Typography } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { PLAN_OWNER_VIEW } from '../../../graphql/queries/planOwners';
import { PLAN_OWNER_DELETE } from '../../../graphql/mutations/planOwners';
import CardLayout from '../../../layouts/CardLayout';

const { Title } = Typography;

const PlanOwnerView: React.FC = () => {
  const { message, modal } = App.useApp();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const { loading, data, refetch } = useQuery(PLAN_OWNER_VIEW, {
    variables: { id },
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    refetch();
    const handleFocus = () => refetch();
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [refetch]);

  const [deletePlanOwner, { loading: deleteLoading }] = useMutation(PLAN_OWNER_DELETE, {
    onCompleted: () => {
      message.success('Plan owner successfully deleted');
      navigate('/admin/plan-owners');
    },
    onError: error => {
      message.error(error.message);
    },
  });

  const handleDelete = () => {
    modal.confirm({
      title: 'Delete Plan Owner',
      content: 'Are you sure you want to delete this plan owner?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: () => deletePlanOwner({ variables: { id } }),
    });
  };

  if (loading) {
    return (
      <Row justify="center" align="middle" style={{ minHeight: '100dvh' }}>
        <Spin size="large" />
      </Row>
    );
  }

  const planOwner = data?.planOwner;

  if (!planOwner) {
    return null;
  }

  return (
    <CardLayout>
      <Row gutter={[16, 24]}>
        <Col xs={24}>
          <Space wrap>
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => navigate(`/admin/plan-owner/edit/${id}`)}>
              Edit
            </Button>
            <Button
              type="primary"
              danger
              icon={<DeleteOutlined />}
              onClick={handleDelete}
              loading={deleteLoading}>
              Delete
            </Button>
          </Space>
        </Col>

        <Col xs={24}>
          <Descriptions title="Plan Owner Information" bordered column={1} layout="horizontal">
            <Descriptions.Item label="Company Name">{planOwner.name}</Descriptions.Item>
            <Descriptions.Item label="CEO">{planOwner.ceo}</Descriptions.Item>
            <Descriptions.Item label="Phone">{planOwner.phone}</Descriptions.Item>
            <Descriptions.Item label="Email">{planOwner.email}</Descriptions.Item>
            <Descriptions.Item label="Website">
              {planOwner.webSite && (
                <a href={planOwner.webSite} target="_blank" rel="noopener noreferrer">
                  {planOwner.webSite}
                </a>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="About">{planOwner.about}</Descriptions.Item>
          </Descriptions>
        </Col>

        {planOwner.logo && (
          <Col xs={24} sm={12} md={8}>
            <Title level={5}>Logo</Title>
            <Image src={planOwner.logo} alt="Logo" style={{ maxWidth: '100%' }} />
          </Col>
        )}

        {planOwner.photos && planOwner.photos.length > 0 && (
          <Col xs={24}>
            <Title level={5}>Photos</Title>
            <Image.PreviewGroup>
              <Row gutter={[8, 8]}>
                {planOwner.photos.map((photo: string, index: number) => (
                  <Col key={index} xs={24} sm={12} md={8} lg={6}>
                    <Image
                      src={photo}
                      alt={`Photo ${index + 1}`}
                      style={{ width: '100%', objectFit: 'cover' }}
                    />
                  </Col>
                ))}
              </Row>
            </Image.PreviewGroup>
          </Col>
        )}
      </Row>
    </CardLayout>
  );
};

export default PlanOwnerView;
